import { TableRow } from '@sam/components';
import { createLanguageMap, CustomerArticle, SimpleUser, Task, UnitCode } from 'shared';
import { getUserNameForSimpleUser } from '../user/User.utils';
import { NavigateFunction } from 'react-router-dom';
import { Language } from 'shared/src/Language/Language.types';
import i18n from '../../i18n/i18n';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Util method to convert tasks into tableEntries
 * @param tasks list of all tasks to display
 * @param users list of simpleUsers to get the names from
 * @param navigate NavigateFunction
 * @returns  Array of converted TableRows
 */
export const convertTasksIntoTableEntries = (
  tasks: Task[],
  users: SimpleUser[],
  navigate: NavigateFunction,
): TableRow[] => {
  return tasks.map((task) => ({
    id: task.id,
    onClick: () => navigate('/task/create', { state: { task } }),
    content: [
      task.title,
      task?.updatedAt?.toLocaleDateString() || '',
      getUserNameForSimpleUser(task?.updatedBy || '', users),
      <div>
        <IconComponent
          type={IconType.EDIT}
          title={i18n.t('general.icons.edit')}
          width={20}
          onClick={(evt) => {
            evt.stopPropagation();
            navigate('/task/create', { state: { task } });
          }}
        />
      </div>,
    ],
  }));
};

/**
 * Helper method to generate an empty task
 * @param override override set attributes as needed
 * @returns  generated Task
 */
export const generateEmptyTask = (override?: Partial<Task>): Task => ({
  id: undefined!,
  createdBy: undefined!,
  translations: new Map<Language, string>(),
  title: '',
  updatedAt: undefined!,
  updatedBy: undefined!,
  identifier: '',
  lastUpdate: undefined!,
  price: 0,
  unit: '',
  amount: 1,
  annotation: '',
  standard: false,
  disabled: false,
  processingFee: false,
  unitCode: UnitCode.HOUR,
  ...override,
});

/**
 * Util method to combine tasks and customerArticles into a single task array
 * @param allTasks all standard tasks to display
 * @param customerArticles all customerArticles
 * @param customerId id of the customer to find the correct customerArticles
 * @param officeId office to filter customerArticles for (One customer can have multiple articles for different offices)
 * @param includeTimeTasks specifies if Hour and day articles are included
 * @returns Array of valid tasks
 */
export const combineStandardAndCustomerTasks = (
  allTasks: Task[],
  customerArticles: CustomerArticle[],
  customerId: string | undefined,
  officeId: string | undefined,
  includeTimeTasks?: boolean,
): Task[] => {
  const combinedTasks: Task[] = [];

  //Remove time based tasks
  const standardTasks: Task[] = allTasks.filter(
    (task) =>
      includeTimeTasks ||
      (task.unitCode != UnitCode.HOUR && task.unitCode !== UnitCode.WORKING_DAY),
  );
  combinedTasks.push(...standardTasks);

  //Convert customerArticles to task if customerId and officeId is provided
  if (customerId && officeId) {
    const customerTasks: Task[] = customerArticles
      .filter(
        (article) => article.customerId === customerId && article.officeIds?.includes(officeId),
      )
      .map((article): Task => {
        const translation: Map<Language, string> = createLanguageMap(article.description);

        return {
          id: article.id,
          amount: 1,
          annotation: article.annotation || '',
          translations: translation,
          identifier: article.partnumber || '',
          price: article.price || 0,
          standard: false,
          processingFee: false,
          title: article.description || '',
          unit: article.unit || '',
          unitCode: article.unitCode || '',
          disabled: false,
        };
      });
    combinedTasks.push(...customerTasks);
  }
  return combinedTasks;
};
